import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { MARK_NOTIFICATION_AS_READ } from '../../graphQLDefs/Notifications/mutations';
import type { MarkAsReadInput } from '../../generated/graphql';

interface UseMarkNotificationsAsReadOptions {
  onSuccess?: (notificationIds: string[]) => void;
  onError?: ((error: Error) => void) | undefined;
}

export const useMarkNotificationsAsRead = (options: UseMarkNotificationsAsReadOptions = {}) => {
  const { onSuccess, onError } = options;

  const [markNotificationAsReadMutation, { loading, error }] = useMutation(
    MARK_NOTIFICATION_AS_READ,
    {
      onError: error => {
        console.error('Mark notification as read error:', error);
        onError?.(error);
      },
    }
  );

  const markAsRead = useCallback(
    async (notificationIds: string[]) => {
      try {
        if (!notificationIds || notificationIds.length === 0) {
          throw new Error('No notification IDs provided');
        }

        const markAsReadInput: MarkAsReadInput = {
          notificationId: notificationIds[0], // Use first ID for single notification marking
        };

        const result = await markNotificationAsReadMutation({
          variables: { data: markAsReadInput },
        });

        if (result.data?.markNotificationAsRead.__typename === 'MarkAsRead') {
          onSuccess?.(notificationIds);
        } else if (result.data?.markNotificationAsRead.__typename === 'Error') {
          throw new Error(result.data.markNotificationAsRead.message);
        }

        return result;
      } catch (error) {
        console.error('Failed to mark notifications as read:', error);
        throw error;
      }
    },
    [markNotificationAsReadMutation, onSuccess]
  );

  return {
    markAsRead,
    loading,
    error,
  };
};
